import classNames from 'classnames';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useAvailabilityStatusLabel } from './AvailabilityStatusPill.hook';

export type tAvailabilityStatusDotProps = {
  isActive: boolean;
  /** Size/positioning utilities — the dot only owns color + shape. */
  className?: string;
  /**
   * Set when the dot is the ONLY state signal in view (e.g. the collapsed
   * avatar corner). The dot then gets `role="img"` so the label is actually
   * announced (aria-label on a role-less span is ignored by screen readers).
   * Omit when visible text accompanies it — the dot is then decorative and
   * hidden from the accessibility tree.
   */
  ariaLabel?: string;
  /**
   * U6 — a contextual (P2) alert owns the corner instead of availability.
   * The avatar carries exactly ONE dot, so the alert re-tones the existing
   * marker rather than stacking a second one beside it; an alert tone asserts
   * nothing about availability, which is why it may show before the
   * availability provider has loaded.
   */
  isAlerting?: boolean;
  /**
   * U6 — makes the dot a real control (Tab-reachable, Enter/Space) instead of
   * a mouse-only target. Deliberately a `role="button"` span and not a
   * `<button>`: the avatar marker renders inside react-daisyui's
   * `Dropdown.Toggle`, which is itself a `<button>`, and nesting one button
   * inside another is invalid DOM that React rejects at runtime. Activation
   * stops propagation so opening the alert does not also toggle the dropdown.
   */
  onActivate?: () => void;
};

/**
 * Availability dot, themed with DaisyUI tokens so both app themes render it
 * correctly. The inactive state uses `bg-base-content/40` (not `bg-base-300`)
 * so it stays visible against dark-theme surfaces. Shared by the pill below
 * and the avatar-corner dots in SidebarAvatar / Dashboard Member so the
 * colors cannot drift (RET-1350).
 */
export function AvailabilityStatusDot(props: tAvailabilityStatusDotProps) {
  const isControl = typeof props.onActivate === 'function';

  function activate(event: MouseEvent | KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    props.onActivate?.();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    // Space must be intercepted, or the key scrolls the page underneath.
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    activate(event);
  }

  let role: string | undefined;
  if (isControl) role = 'button';
  else if (props.ariaLabel) role = 'img';

  return (
    <span
      role={role}
      tabIndex={isControl ? 0 : undefined}
      aria-label={props.ariaLabel}
      aria-hidden={role ? undefined : true}
      onClick={isControl ? activate : undefined}
      onKeyDown={isControl ? handleKeyDown : undefined}
      className={classNames(
        'rounded-full',
        {
          'bg-warning': props.isAlerting,
          'bg-success': !props.isAlerting && props.isActive,
          'bg-base-content/40': !props.isAlerting && !props.isActive,
          'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-warning':
            isControl,
        },
        props.className,
      )}
    />
  );
}

export type tAvailabilityStatusPillProps = {
  isActive: boolean;
};

/**
 * The single rendering of a user's availability state (RET-1350). Every
 * surface that shows the flag — avatar menu, Dashboard advisor cards, and
 * future Team-tab rows (RET-1351) — renders this pill so the word pair
 * (Activo/Inactivo) and colors can never drift apart again. The labels live
 * in `common/userAvailability`, shared with `useUserAvailability`.
 */
export default function AvailabilityStatusPill(
  props: tAvailabilityStatusPillProps,
) {
  const label = useAvailabilityStatusLabel(props.isActive);

  return (
    <span
      className={classNames('badge badge-sm gap-1.5 border-0 font-medium', {
        'bg-success/10 text-success': props.isActive,
        'bg-base-200 text-base-content/70': !props.isActive,
      })}
    >
      <AvailabilityStatusDot isActive={props.isActive} className="w-2 h-2" />
      {label}
    </span>
  );
}
